import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Sidebar } from "@/components/sidebar";

import { InlineNodeField } from "../fields/InlineNodeField";

import type { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import type { NodeModel } from "@/models/nodes/types";

type Props = {
  node: LabelNodeModel;
  parent: NodeModel;
  leadingAction?: React.ReactNode;
};

export const SymbolIndexField = observer(function SymbolIndexField({
  node,
  parent,
  leadingAction,
}: Props) {
  const { doc, inlineNode } = node;
  const t = useTranslations();

  if (!inlineNode) {
    return null;
  }

  const resetSelectionAndMove = () => {
    const { cursor, selection } = doc.cursorContext();

    if (selection.isSelected(parent)) {
      selection.resetSelection();
      cursor.moveAfterNode(parent);
    }
  };

  const handleOnRemove = () => {
    doc.transact(
      () => {
        doc.removeNode(node);
      },
      "removeLabelNode",
      { runInAction: true },
    );
    resetSelectionAndMove();
  };

  return (
    <InlineNodeField
      node={inlineNode}
      label={node.name ?? t(`sidebar.activeNode.fields.labels.${node.position}`)}
      onLabelMouseDown={resetSelectionAndMove}
      actionClassName={leadingAction ? "flex items-center" : undefined}
      contentClassName={leadingAction ? "pr-[66px]" : undefined}
      action={
        <>
          {leadingAction}
          <Sidebar.Button
            type="button"
            title={t("sidebar.actions.removeField", {
              field: node.name ?? t(`sidebar.activeNode.fields.tooltips.labels.${node.position}`),
            })}
            icon={<div className="w-[10px] h-[1px] bg-document-text rounded-8" />}
            className="w-6 h-6 min-h-6"
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
            onClick={handleOnRemove}
          />
        </>
      }
    />
  );
});
